import React, { useEffect, useState } from "react";
import api, { fmtInt } from "@/api";
import { useI18n } from "@/i18n";
import { useCurrency } from "@/currency";
import { useShowPrices } from "@/showPrices";
import { useAuth, can } from "@/auth";
import { X, Trophy, XCircle, Clock, PauseCircle, MinusCircle } from "@phosphor-icons/react";

/* ---------------------------------------------------------------------------
   OutcomeBadge — small pill showing current tracking status, click → editor.
   Used in the Opportunities table.
   --------------------------------------------------------------------------- */
const STATUS_META = {
  open:         { color: "var(--text-tertiary)", bg: "var(--bg-subtle)",   Icon: MinusCircle },
  in_progress:  { color: "#0D71DD",              bg: "rgba(13,113,221,0.10)", Icon: Clock },
  won:          { color: "#34A853",              bg: "rgba(52,168,83,0.12)",   Icon: Trophy },
  lost:         { color: "var(--accent)",        bg: "rgba(237,32,39,0.10)",  Icon: XCircle },
  hold:         { color: "#F59E0B",              bg: "rgba(245,158,11,0.12)", Icon: PauseCircle },
  expired:      { color: "var(--text-tertiary)", bg: "var(--bg-subtle)",   Icon: MinusCircle },
};

const STATUS_LABEL = {
  fr: { open: "Ouverte", in_progress: "En cours", won: "Gagnée", lost: "Perdue", hold: "En attente", expired: "Expirée" },
  en: { open: "Open",    in_progress: "In progress", won: "Won",  lost: "Lost",  hold: "On hold",     expired: "Expired" },
};

const LOST_REASONS_FR = ["Prix", "Relation", "Technique", "Timing", "Autre"];
const LOST_REASONS_EN = ["Price", "Relationship", "Technical", "Timing", "Other"];

export function OutcomeBadge({ opp, lang, onChange, outcome: outcomeProp }) {
  const [outcome, setOutcome] = useState(outcomeProp || null);
  const [open, setOpen] = useState(false);

  // Outcome is provided by the parent (fetched in one batched call) — keep in
  // sync when it changes. No per-row API call: with 900+ rows that floods the
  // server and triggers 429 rate-limiting.
  useEffect(() => {
    setOutcome(outcomeProp || null);
  }, [outcomeProp]);

  const status = outcome?.status || "open";
  const meta = STATUS_META[status];
  const Icon = meta.Icon;
  const label = STATUS_LABEL[lang][status];

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-mono uppercase tracking-wide transition-all hover:opacity-80"
        style={{ background: meta.bg, color: meta.color, border: `1px solid ${meta.color}` }}
        data-testid={`outcome-badge-${opp.id}`}
      >
        <Icon size={11} weight="fill" />
        {label}
      </button>
      {open && <OutcomeEditor opp={opp} outcome={outcome || { status: "open" }} lang={lang}
                              onClose={() => setOpen(false)}
                              onSaved={(o) => { setOutcome(o); setOpen(false); onChange && onChange(o); }} />}
    </>
  );
}


function OutcomeEditor({ opp, outcome, lang, onClose, onSaved }) {
  const { user } = useAuth();
  const { showPrices } = useShowPrices();
  const editable = can(user, "sales");
  const [form, setForm] = useState({
    status: outcome.status || "open",
    actual_value_eur: outcome.actual_value_eur || "",
    close_date: outcome.close_date || "",
    lost_reason: outcome.lost_reason || "",
    notes: outcome.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const LABELS = lang === "fr" ? {
    title: "Suivi de l'opportunité",
    status: "Statut",
    actual: "Valeur réelle (€)",
    close_date: "Date de clôture",
    lost_reason: "Raison de perte",
    notes: "Notes",
    save: "Enregistrer",
    cancel: "Annuler",
    forecast_label: "Prévision M3",
    bias_label: "Écart vs prévision",
    not_editable: "Lecture seule (rôle viewer)",
  } : {
    title: "Opportunity outcome",
    status: "Status",
    actual: "Actual value (€)",
    close_date: "Close date",
    lost_reason: "Lost reason",
    notes: "Notes",
    save: "Save",
    cancel: "Cancel",
    forecast_label: "M3 forecast",
    bias_label: "Bias vs forecast",
    not_editable: "Read-only (viewer role)",
  };
  const REASONS = lang === "fr" ? LOST_REASONS_FR : LOST_REASONS_EN;

  const save = async () => {
    if (!editable) return;
    setSaving(true);
    const body = { status: form.status };
    if (form.actual_value_eur !== "") body.actual_value_eur = parseFloat(form.actual_value_eur);
    if (form.close_date) body.close_date = form.close_date;
    if (form.lost_reason) body.lost_reason = form.lost_reason;
    if (form.notes) body.notes = form.notes;
    try {
      const r = await api.put(`/opportunities/${encodeURIComponent(opp.opp_key)}/outcome`, body);
      onSaved(r.data);
    } finally { setSaving(false); }
  };

  const forecast = opp.m3_weighted || 0;
  const actualNum = parseFloat(form.actual_value_eur);
  const bias = (form.status === "won" && !isNaN(actualNum) && forecast > 0)
    ? ((actualNum - forecast) / forecast) * 100
    : null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4"
         style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(6px)" }}
         onClick={onClose}>
      <div className="tile p-5 md:p-7 w-full max-w-md fade-in"
           onClick={(e) => e.stopPropagation()}
           style={{ boxShadow: "var(--shadow-lg)" }}
           data-testid="outcome-editor">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-[18px] font-semibold tracking-tight">{LABELS.title}</h3>
            <div className="text-[11px] font-mono mt-0.5" style={{ color: "var(--text-tertiary)" }}>
              {opp.id} · {opp.operator} / {opp.sub_system}
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-[var(--bg-subtle)]"><X size={16} /></button>
        </div>

        {/* Quick status grid */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          {Object.keys(STATUS_META).map((s) => {
            const m = STATUS_META[s];
            const Icon = m.Icon;
            const active = form.status === s;
            return (
              <button
                key={s}
                onClick={() => editable && setForm({ ...form, status: s })}
                disabled={!editable}
                className={`flex flex-col items-center gap-1 px-2 py-2.5 rounded-[10px] transition-all`}
                style={{
                  background: active ? m.bg : "var(--bg-subtle)",
                  border: `1px solid ${active ? m.color : "transparent"}`,
                  color: active ? m.color : "var(--text-secondary)",
                  opacity: editable ? 1 : 0.6,
                }}
                data-testid={`outcome-status-${s}`}
              >
                <Icon size={14} weight={active ? "fill" : "regular"} />
                <span className="text-[10.5px] font-mono uppercase">{STATUS_LABEL[lang][s]}</span>
              </button>
            );
          })}
        </div>

        {showPrices && (form.status === "won" || form.status === "in_progress") && (
          <div className="mb-3">
            <div className="label-tech mb-1">{LABELS.actual}</div>
            <input
              type="number"
              value={form.actual_value_eur}
              onChange={(e) => setForm({ ...form, actual_value_eur: e.target.value })}
              className="input-tech"
              placeholder={`${Math.round(forecast).toLocaleString()} (forecast)`}
              disabled={!editable}
              data-testid="outcome-actual"
            />
            {bias !== null && (
              <div className="text-[11px] mt-1.5 font-mono" style={{ color: bias > 0 ? "var(--success)" : "var(--accent)" }}>
                {LABELS.bias_label}: {bias > 0 ? "+" : ""}{bias.toFixed(1)}% vs {LABELS.forecast_label} {Math.round(forecast).toLocaleString()} €
              </div>
            )}
          </div>
        )}

        {form.status === "lost" && (
          <div className="mb-3">
            <div className="label-tech mb-1">{LABELS.lost_reason}</div>
            <div className="flex flex-wrap gap-1.5">
              {REASONS.map((r) => (
                <button
                  key={r}
                  onClick={() => setForm({ ...form, lost_reason: r })}
                  className="px-2.5 py-1 text-[11.5px] rounded-full transition-colors"
                  style={{
                    background: form.lost_reason === r ? "var(--accent)" : "var(--bg-subtle)",
                    color: form.lost_reason === r ? "white" : "var(--text-secondary)",
                  }}
                  data-testid={`outcome-reason-${r}`}
                  disabled={!editable}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
        )}

        {(form.status === "won" || form.status === "lost" || form.status === "expired") && (
          <div className="mb-3">
            <div className="label-tech mb-1">{LABELS.close_date}</div>
            <input
              type="date"
              value={form.close_date}
              onChange={(e) => setForm({ ...form, close_date: e.target.value })}
              className="input-tech"
              disabled={!editable}
            />
          </div>
        )}

        <div className="mb-4">
          <div className="label-tech mb-1">{LABELS.notes}</div>
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={2}
            className="input-tech"
            disabled={!editable}
            data-testid="outcome-notes"
          />
        </div>

        {!editable ? (
          <div className="text-[11.5px] text-center py-2" style={{ color: "var(--text-tertiary)" }}>{LABELS.not_editable}</div>
        ) : (
          <div className="flex gap-2 justify-end">
            <button onClick={onClose} className="btn-ghost" data-testid="outcome-cancel">{LABELS.cancel}</button>
            <button onClick={save} disabled={saving} className="btn-accent" data-testid="outcome-save">
              {saving ? "…" : LABELS.save}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


/* ---------------------------------------------------------------------------
   ForecastVsActualTile — compact KPI card used on the Dashboard.
   --------------------------------------------------------------------------- */
export function ForecastVsActualTile() {
  const { lang } = useI18n();
  const { fmt } = useCurrency();
  const { showPrices } = useShowPrices();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/forecast-vs-actual").then((r) => setData(r.data)).catch(() => setData({ tracked_count: 0 }));
  }, []);

  const LABELS = lang === "fr" ? {
    title: "Suivi gain / perte",
    none: "Aucune opportunité suivie. Marquez une opportunité comme Gagnée ou Perdue pour activer le suivi.",
    won: "Gagnées",
    lost: "Perdues",
    bias: "Écart prévision",
    bias_help: "vs M3 prévu",
    accurate: "🎯 Prévisions justes",
    optimistic: "⚠️ Trop optimiste",
    pessimistic: "📈 Sous-estimé",
  } : {
    title: "Win / loss tracking",
    none: "No tracked opportunities yet. Mark one as Won or Lost to activate tracking.",
    won: "Won",
    lost: "Lost",
    bias: "Forecast bias",
    bias_help: "vs forecast M3",
    accurate: "🎯 On target",
    optimistic: "⚠️ Too optimistic",
    pessimistic: "📈 Under-forecast",
  };

  if (!data) return null;
  if (!data.tracked_count) {
    return (
      <div className="tile p-5 md:p-6" data-testid="forecast-tile">
        <div className="label-tech mb-2 flex items-center gap-2">
          <Trophy size={13} weight="fill" style={{ color: "var(--accent)" }} />
          {LABELS.title}
        </div>
        <div className="text-[13px] py-3" style={{ color: "var(--text-secondary)" }}>{LABELS.none}</div>
      </div>
    );
  }

  const bias = data.bias_pct;
  const verdict = bias == null ? null
    : Math.abs(bias) < 10 ? LABELS.accurate
    : bias < 0 ? LABELS.optimistic
    : LABELS.pessimistic;
  const biasColor = bias == null ? "var(--text-tertiary)"
    : Math.abs(bias) < 10 ? "var(--success)"
    : bias < 0 ? "var(--accent)"
    : "var(--info)";

  return (
    <div className="tile p-5 md:p-6" data-testid="forecast-tile">
      <div className="label-tech mb-3 flex items-center gap-2">
        <Trophy size={13} weight="fill" style={{ color: "var(--accent)" }} />
        {LABELS.title}
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className="text-[10.5px] font-mono uppercase" style={{ color: "var(--text-tertiary)" }}>{LABELS.won}</div>
          <div className="num text-[22px] font-semibold mt-0.5" style={{ color: "var(--success)" }}>{data.by_status.won}</div>
          {showPrices && <div className="text-[11px] font-mono mt-0.5" style={{ color: "var(--text-secondary)" }}>{fmt(data.won_value_eur)}</div>}
        </div>
        <div>
          <div className="text-[10.5px] font-mono uppercase" style={{ color: "var(--text-tertiary)" }}>{LABELS.lost}</div>
          <div className="num text-[22px] font-semibold mt-0.5" style={{ color: "var(--accent)" }}>{data.by_status.lost}</div>
          {showPrices && <div className="text-[11px] font-mono mt-0.5" style={{ color: "var(--text-secondary)" }}>{fmt(data.lost_forecast_eur)}</div>}
        </div>
        <div>
          <div className="text-[10.5px] font-mono uppercase" style={{ color: "var(--text-tertiary)" }}>{LABELS.bias}</div>
          <div className="num text-[22px] font-semibold mt-0.5" style={{ color: biasColor }}>
            {bias == null ? "—" : `${bias > 0 ? "+" : ""}${bias.toFixed(0)}%`}
          </div>
          <div className="text-[11px] font-mono mt-0.5" style={{ color: "var(--text-secondary)" }}>{LABELS.bias_help}</div>
        </div>
      </div>
      {verdict && (
        <div className="mt-3 pt-3 text-[12px] font-medium" style={{ borderTop: "1px solid var(--border)", color: biasColor }}>
          {verdict}
        </div>
      )}
    </div>
  );
}
